// Curated Google Fonts catalog for per-block font selection.
// Each font lists the weights we make available in the picker.

export type FontCategory = 'sans' | 'serif' | 'display' | 'handwriting' | 'mono';

export interface FontDef {
  /** Exact Google Fonts family name (also used as CSS font-family). */
  family: string;
  category: FontCategory;
  /** Weights to load and expose in the weight picker. */
  weights: number[];
  italic?: boolean;
}

export const FONTS: FontDef[] = [
  // Sans
  { family: 'Inter',              category: 'sans', weights: [100,200,300,400,500,600,700,800,900], italic: true },
  { family: 'Roboto',             category: 'sans', weights: [100,300,400,500,700,900], italic: true },
  { family: 'Open Sans',          category: 'sans', weights: [300,400,500,600,700,800], italic: true },
  { family: 'Lato',               category: 'sans', weights: [100,300,400,700,900], italic: true },
  { family: 'Poppins',            category: 'sans', weights: [100,200,300,400,500,600,700,800,900], italic: true },
  { family: 'Montserrat',         category: 'sans', weights: [100,200,300,400,500,600,700,800,900], italic: true },
  { family: 'Nunito',             category: 'sans', weights: [200,300,400,500,600,700,800,900], italic: true },
  { family: 'Raleway',            category: 'sans', weights: [100,200,300,400,500,600,700,800,900], italic: true },
  { family: 'Work Sans',          category: 'sans', weights: [100,200,300,400,500,600,700,800,900], italic: true },
  { family: 'DM Sans',            category: 'sans', weights: [400,500,700], italic: true },
  { family: 'Plus Jakarta Sans',  category: 'sans', weights: [200,300,400,500,600,700,800], italic: true },
  { family: 'Manrope',            category: 'sans', weights: [200,300,400,500,600,700,800] },
  { family: 'Quicksand',          category: 'sans', weights: [300,400,500,600,700] },

  // Serif
  { family: 'Playfair Display',   category: 'serif', weights: [400,500,600,700,800,900], italic: true },
  { family: 'Merriweather',       category: 'serif', weights: [300,400,700,900], italic: true },
  { family: 'Lora',               category: 'serif', weights: [400,500,600,700], italic: true },
  { family: 'PT Serif',           category: 'serif', weights: [400,700], italic: true },
  { family: 'Cormorant Garamond', category: 'serif', weights: [300,400,500,600,700], italic: true },
  { family: 'Crimson Text',       category: 'serif', weights: [400,600,700], italic: true },
  { family: 'EB Garamond',        category: 'serif', weights: [400,500,600,700,800], italic: true },
  { family: 'Source Serif 4',     category: 'serif', weights: [200,300,400,500,600,700,800,900] },
  { family: 'Libre Baskerville',  category: 'serif', weights: [400,700], italic: true },

  // Display
  { family: 'Bebas Neue',         category: 'display', weights: [400] },
  { family: 'Oswald',             category: 'display', weights: [200,300,400,500,600,700] },
  { family: 'Anton',              category: 'display', weights: [400] },
  { family: 'Archivo Black',      category: 'display', weights: [400] },
  { family: 'Righteous',          category: 'display', weights: [400] },
  { family: 'Abril Fatface',      category: 'display', weights: [400] },
  { family: 'Bungee',             category: 'display', weights: [400] },
  { family: 'Permanent Marker',   category: 'display', weights: [400] },

  // Handwriting
  { family: 'Dancing Script',     category: 'handwriting', weights: [400,500,600,700] },
  { family: 'Pacifico',           category: 'handwriting', weights: [400] },
  { family: 'Caveat',             category: 'handwriting', weights: [400,500,600,700] },
  { family: 'Great Vibes',        category: 'handwriting', weights: [400] },
  { family: 'Sacramento',         category: 'handwriting', weights: [400] },
  { family: 'Satisfy',            category: 'handwriting', weights: [400] },
  { family: 'Shadows Into Light', category: 'handwriting', weights: [400] },

  // Mono
  { family: 'JetBrains Mono',     category: 'mono', weights: [100,200,300,400,500,600,700,800], italic: true },
  { family: 'Fira Code',          category: 'mono', weights: [300,400,500,600,700] },
  { family: 'Source Code Pro',    category: 'mono', weights: [200,300,400,500,600,700,800,900], italic: true },
  { family: 'IBM Plex Mono',      category: 'mono', weights: [100,200,300,400,500,600,700], italic: true },
];

export const FONT_BY_FAMILY: Record<string, FontDef> = Object.fromEntries(
  FONTS.map(f => [f.family, f]),
);

export const CATEGORY_LABELS: Record<FontCategory, string> = {
  sans: 'Sans Serif',
  serif: 'Serif',
  display: 'Display',
  handwriting: 'Handwriting',
  mono: 'Monospace',
};

export const WEIGHT_OPTIONS: { v: number; label: string }[] = [
  { v: 100, label: 'Thin' },
  { v: 300, label: 'Light' },
  { v: 400, label: 'Regular' },
  { v: 500, label: 'Medium' },
  { v: 600, label: 'Semibold' },
  { v: 700, label: 'Bold' },
  { v: 900, label: 'Black' },
];

const loaded = new Set<string>();

/**
 * Inject a Google Fonts <link> for a given family/weight set.
 * Safe to call repeatedly — each unique (family + weight set) is loaded once.
 * No-op when running outside the browser.
 */
export function loadGoogleFont(family: string, weights: number[] = [400], italic = false): void {
  if (typeof document === 'undefined' || !family) return;
  const def = FONT_BY_FAMILY[family];
  const ws = (def?.weights?.length ? weights.filter(w => def!.weights.includes(w)) : weights);
  const finalWeights = (ws.length ? ws : [400]).sort((a,b) => a-b);
  const key = `${family}|${finalWeights.join(',')}|${italic ? 'i' : ''}`;
  if (loaded.has(key)) return;
  loaded.add(key);

  const familyParam = family.replace(/ /g, '+');
  const axis = italic
    ? `ital,wght@${finalWeights.flatMap(w => [`0,${w}`, `1,${w}`]).join(';')}`
    : `wght@${finalWeights.join(';')}`;
  const href = `https://fonts.googleapis.com/css2?family=${familyParam}:${axis}&display=swap`;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.dataset.linktreeFont = family;
  document.head.appendChild(link);
}
